import React, { useState, useEffect, useContext } from "react";
import styled from "styled-components";
import * as d3 from "d3";

import ArrangeInColumns from "common/layouts/ArrangeInColumns";
import SelectInputWithIcon from "common/SelectInputWithIcon";
import ActionsContext from "common/ActionsContext";
import BqnContext from "common/BqnContext";
import { NVLineChartDownsampled } from "common/graphs/DownSampling";
import Request from "common/Request";
import { atBqnTarget, retrieveRange } from "common/api";
import RangeSliderInput from "common/RangeSliderInput";
import {
  PERIOD_TO_HOURS,
  PERIOD_TO_LINES,
  altPeriodScopeChoices,
  HOURS_TO_PERIOD,
} from "common/constants";
import { parseStatsRtxData} from "./api";
import useDebouncer from "common/hooks/debouncer";
import useHoursOfReturnView from "common/hooks/useHoursOfReturnView";


const Wrapper = styled.div`
  width: 100%;
  position: relative;
`;

const SelectWrapper = styled.div`
  width: 150px;
`;

const ChartContainer = styled.div`
  display: flex;
  min-height: 500px;
  margin-top: 20px;

  svg.nvd3-svg .nv-axis path.domain {
    stroke-opacity: 0;
  }

  .nv-y.nv-axis .nv-axisMaxMin {
    display: none;
  }

  .nv-y.nv-axis .tick.zero line {
    stroke: transparent !important;
  }

  .nvd3 .nv-groups path.nv-line {
    stroke-width: 1.1;
  }
`;

const RangeContainer = styled.div`
  margin: 20px 0;
`;

export const useStatsRtxData = (
  period = 'Day',
  firstRequest = null
) => {
  const actions = useContext(ActionsContext);
  const [request, setRequest] = useState(firstRequest);
  const [initialRange, setInitialRange] = useState();
  const [range, setRange] = useState();
  const debounce = useDebouncer(500 /*ms*/);
  const { bqnId, clusterStatus } = useContext(BqnContext);
  const mode = clusterStatus?.mode;

  const doRequest = (period) => {
    setRange(undefined);
    const hours = PERIOD_TO_HOURS[period];
    return retrieveRange(hours, mode, bqnId).then(({ from, to }) => {
      setInitialRange({ from, to });
      return ifCl
        .run(
          atBqnTarget(
            `show statistics retransmission lines ${PERIOD_TO_LINES[period]}`,
            mode,
            bqnId
          )
        )
        .then((response) => {
          return parseStatsRtxData(response, period, from, to);
        });
    });
  };

  const doLoad = () => {
    setRequest(doRequest(period));
  };

  // Nothing is asked while the BQN context has not loaded the BQN selected:
  // the commands would not carry its option, and their responses would be
  // those of the whole cluster. The effect runs again when it does.
  useEffect(() => {
    if (bqnId === undefined) {
      return;
    }

    debounce(() => doLoad());
  }, [period, mode, bqnId]);

  useEffect(() => actions.recv('do-load', doLoad), [doLoad]);
  return { request, doLoad, initialRange, range, setRange };
};

const StatsRtxReport = ({ returnView = null }) => {
  const hoursOfReturnView = useHoursOfReturnView(returnView);
  const [period, setPeriod] = useState(
    HOURS_TO_PERIOD[hoursOfReturnView] ?? "Day",
  );
  const { request, initialRange, range, setRange } = useStatsRtxData(period, null);
  return (
    <Wrapper>
      <ArrangeInColumns rowGap="0">
        <SelectWrapper>
          <SelectInputWithIcon
            title="Date Range"
            name="Date Range"
            icon="date_range"
            selected={period}
            onChange={({ target }) => setPeriod(target.value)}
            options={altPeriodScopeChoices}
          />
        </SelectWrapper>
      </ArrangeInColumns>
      <ChartContainer>
        <Request during={request}>
          {({ items, fields }) => {
            const selectedRange = range || initialRange;

            return (
              <NVLineChartDownsampled
                xField="time"
                items={items}
                range={selectedRange}
                yAxisUnits={"%"}
                yAxisFormat={d3.format(".1f")}
                xAxisFormat={d3.timeFormat("%m/%d %H:%M")}
                fields={["Rtx%-Access"].map((field) => {
                  return { name: field, label: field };
                })}
                yAxisTopGap={0.05}
                yAxisTicksNumber={8}
                xAxisTicksNumber={8}
                yTooltipFormat={(d) => d3.format(".2f")(d) + " %"}
                fixedYRange={[0, 1]}
              />
            );
          }}
        </Request>
      </ChartContainer>
      <RangeContainer>
        {initialRange && (
          <RangeSliderInput
            {...initialRange}
            onChange={(value) => setRange(value)}
          />
        )}
      </RangeContainer>
    </Wrapper>
  );
};

export default StatsRtxReport;
